import { useState, useMemo, useEffect, useCallback } from "react";
import { useSearchParams } from "react-router-dom";
import { SearchInput } from "../components/SearchInput";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import { useDataLoader } from "../hooks/useDataLoader";
import { loadFestivals } from "../utils/dataLoader";
import type { Festival } from "../types/data";
import { SEO } from "../components/SEO";
import { routeSeo } from "../content/seoRoutes";

export function FestivalsPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const selectedYear = Number(searchParams.get('year')) || 2026;
  const urlSearchTerm = searchParams.get('q') || "";

  const [searchTerm, setSearchTerm] = useState(urlSearchTerm);
  const [debouncedSearch, setDebouncedSearch] = useState(urlSearchTerm);
  const { data: festivals, loading, error } = useDataLoader(loadFestivals);

  // Debounce search term to avoid lagging while typing
  useEffect(() => {
    const id = setTimeout(() => {
      setDebouncedSearch(searchTerm);
      setSearchParams(prev => {
        const next = new URLSearchParams(prev);
        const trimmed = searchTerm.trim();
        if (trimmed) next.set('q', trimmed);
        else next.delete('q');
        return next;
      }, { replace: true });
    }, 200);
    return () => clearTimeout(id);
  }, [searchTerm, setSearchParams]);

  // Sync URL search parameters to state on mount/back navigation
  useEffect(() => {
    setSearchTerm(urlSearchTerm);
    setDebouncedSearch(urlSearchTerm);
  }, [urlSearchTerm]);

  const handleYearChange = useCallback((year: number) => {
    setSearchParams(prev => {
      const next = new URLSearchParams(prev);
      next.set('year', year.toString());
      return next;
    }, { replace: true });
  }, [setSearchParams]);

  const filteredFestivals = useMemo(() => {
    const source = festivals ?? [];
    const yearFiltered = source.filter((festival) => festival.year === selectedYear);
    
    if (!debouncedSearch) return yearFiltered;

    const term = debouncedSearch.toLowerCase();
    return yearFiltered.filter(
      (festival) =>
        festival.name.toLowerCase().includes(term) ||
        festival.date.toLowerCase().includes(term) ||
        festival.description.toLowerCase().includes(term) ||
        festival.type.toLowerCase().includes(term)
    );
  }, [debouncedSearch, festivals, selectedYear]);

  const groupedFestivals = useMemo(() => {
    const grouped: { [key: string]: Festival[] } = {};
    filteredFestivals.forEach((festival) => {
      if (!grouped[festival.type]) {
        grouped[festival.type] = [];
      }
      grouped[festival.type].push(festival);
    });
    return grouped;
  }, [filteredFestivals]);

  const getTypeColor = (type: string) => {
    switch (type) {
      case "Hindu": return "bg-orange-100 text-orange-800 dark:bg-orange-950 dark:text-orange-300";
      case "Islamic": return "bg-green-100 text-green-800 dark:bg-green-950 dark:text-green-300";
      case "Christian": return "bg-blue-100 text-blue-800 dark:bg-blue-950 dark:text-blue-300";
      case "National": return "bg-rose-100 text-rose-800 dark:bg-rose-950 dark:text-rose-300";
      case "Jain": return "bg-purple-100 text-purple-800 dark:bg-purple-950 dark:text-purple-300";
      case "Buddhist": return "bg-amber-100 text-amber-800 dark:bg-amber-950 dark:text-amber-300";
      case "Sikh": return "bg-indigo-100 text-indigo-800 dark:bg-indigo-950 dark:text-indigo-300";
      default: return "bg-gray-100 text-gray-800 dark:bg-gray-800 dark:text-gray-300";
    }
  };

  // SEO structured data
  const structuredData = {
    "@context": "https://schema.org",
    "@type": "Dataset",
    "name": "Indian Festivals and Holidays Database",
    "description": "Complete calendar database of Indian festivals, gazetted holidays, and national holidays with dates and descriptions",
    "keywords": ["Indian festivals", "holidays calendar", "festival dates", "gazetted holidays India"],
    "license": "https://creativecommons.org/licenses/by/4.0/",
    "creator": {
      "@type": "Organization",
      "name": "Indian Codes Hub"
    },
    "distribution": {
      "@type": "DataDownload",
      "contentUrl": `${window.location.origin}/festivals`,
      "encodingFormat": "text/html"
    }
  };

  return (
    <>
      <SEO
        {...routeSeo('festivals')}
        keywords="Indian festivals, holiday calendar, festival dates, gazetted holidays India, national holidays, holiday database"
        structuredData={structuredData}
      />
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
      <div className="text-center mb-8">
        <h1 className="text-2xl sm:text-3xl font-bold text-gray-900 mb-2">
          Festival Calendar {selectedYear}
        </h1>
        <p className="text-sm sm:text-base text-gray-600">
          Explore Indian festivals and national holidays
        </p>
      </div>

      {/* Modern Premium Year Toggle */}
      <div className="flex justify-center space-x-3 mb-8">
        {[2027, 2026, 2025].map((year) => (
          <button
            key={year}
            onClick={() => handleYearChange(year)}
            className={`px-6 py-2.5 rounded-full text-sm font-semibold transition-all duration-300 shadow-sm ${
              selectedYear === year
                ? "bg-gradient-to-r from-orange-500 to-amber-500 text-white shadow-orange-200/50 dark:shadow-none transform scale-105"
                : "bg-white text-gray-600 border border-gray-200 hover:bg-gray-50 hover:border-gray-300"
            }`}
          >
            {year} Festivals
          </button>
        ))}
      </div>

      {loading && (
        <div className="text-center py-8 text-gray-500">Loading festivals...</div>
      )}
      {error && (
        <div className="text-center py-8 text-red-600">Failed to load festivals. Please try again later.</div>
      )}

      {!loading && !error && (
        <>
          <SearchInput
            value={searchTerm}
            onChange={setSearchTerm}
            placeholder="Search festivals by name, date, or type..."
          />

          {Object.entries(groupedFestivals).map(([type, festivals]) => (
            <div key={type} className="mb-8">
              <h2 className="text-xl font-semibold text-gray-800 mb-4">{type} Festivals</h2>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {festivals.map((festival, index) => (
                  <Card key={`${festival.name}-${index}`} className="hover:shadow-lg transition-shadow">
                    <CardHeader className="pb-3">
                      <CardTitle className="flex items-center justify-between">
                        <span className="font-semibold text-gray-900">{festival.name}</span>
                        <Badge className={getTypeColor(festival.type)}>{festival.type}</Badge>
                      </CardTitle>
                    </CardHeader>
                    <CardContent>
                      <div className="space-y-2">
                        <div>
                          <p className="text-xs text-gray-400 font-medium uppercase tracking-wider">Date</p>
                          <p className="font-medium text-gray-750">{festival.date}, {festival.year}</p>
                        </div>
                        <div>
                          <p className="text-xs text-gray-400 font-medium uppercase tracking-wider">Description</p>
                          <p className="text-sm text-gray-600 leading-relaxed">{festival.description}</p>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            </div>
          ))}

          {filteredFestivals.length === 0 && (
            <div className="text-center py-12">
              <p className="text-gray-500">No festivals found matching your search.</p>
            </div>
          )}
        </>
      )}
    </div>
    </>
  );
}